import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { Libraries, type LibraryListResponse } from '@air/api';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { isLibariesKey } from '~/components/LibraryBeta/hooks/queries/useLibraries';
import { reorderLibrariesAcrossPages } from '~/components/v2/utils/libraries/reorderLibrariesAcrossPages';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export type UseLibrariesUpdatePositionsMutationParams = {
  workspaceId: string;
  idsToMove: string[];
  betweenIds: {
    moveBeforeId?: string;
    moveAfterId?: string;
  };
};

/**
 * Mutation hook for updating library positions with optimistic updates.
 *
 * This hook implements a three-phase update strategy for drag-and-drop reordering:
 * 1. onMutate: Immediately reorder libraries in cache (optimistic) for instant UI feedback
 * 2. onError: Roll back to previous state if server update fails
 * 3. onSuccess: Re-sync with server to ensure consistency
 *
 * The optimistic update works across all library queries for the workspace,
 * ensuring consistency regardless of which filters/sorts are active.
 */
export const useLibrariesUpdatePositionsMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useCurrentWorkspace();

  return useMutation({
    mutationFn: async (params: UseLibrariesUpdatePositionsMutationParams) => {
      return callAirApi(() => Libraries.updatePositions(params));
    },
    onMutate: async (variables) => {
      const allPreviousLibraries = queryClient.getQueriesData<InfiniteData<LibraryListResponse> | undefined>({
        predicate: ({ queryKey }) => {
          return isLibariesKey(queryKey) && queryKey[1].workspaceId === variables.workspaceId;
        },
      });

      allPreviousLibraries.forEach(([queryKey]) => {
        queryClient.cancelQueries({ queryKey });
      });

      allPreviousLibraries.forEach(([queryKey, previousData]) => {
        if (!previousData) {
          return;
        }

        const updatedPages = reorderLibrariesAcrossPages({
          pages: previousData.pages,
          idsToMove: variables.idsToMove,
          betweenIds: variables.betweenIds,
        });

        queryClient.setQueryData(queryKey, {
          ...previousData,
          pages: updatedPages,
        });
      });

      return { allPreviousLibraries };
    },
    onError: (error, variables, context) => {
      context?.allPreviousLibraries.forEach(([queryKey, previousData]) => {
        queryClient.setQueryData(queryKey, previousData);
      });

      reportErrorToBugsnag({
        error,
        context: 'Failed to update library positions',
        metadata: {
          key: 'data',
          data: variables,
        },
      });
    },
    onSuccess: () => {
      if (!currentWorkspace?.id) {
        return;
      }

      queryClient.invalidateQueries({
        predicate: (query) => isLibariesKey(query.queryKey) && query.queryKey[1].workspaceId === currentWorkspace.id,
      });
    },
  });
};
